import LayerRenderer from './layer-renderer';
import RenderUtil from './render-util';
//LAZY import {fabric} from 'fabric';
import * as log from 'loglevel';


class MaskRenderer extends LayerRenderer {

    render(layer, context, canvas){

        // apply to full canvas OR individual objects
        if(layer.params.maskLayers && layer.params.maskLayers.length > 0){

            // reset full canvas mask if set previously (happens in editorMode - we cannot check the layer.object as the layers are overwritten on update)
            if(canvas.clipPath){
                delete canvas.clipPath; // reset
            }

            // NEW: absolute masks preserve a refRect area clip by nesting it - ONLY in absolute mode:
            // fabric resolves a nested absolutePositioned clip in the OUTER clip's parent space
            // (fabric.js _drawClipPath), so the canvas coords of the refRect only stay correct under
            // an absolute outer mask. Relative masks keep the legacy behavior (mask replaces the clip).
            let absolute = !!layer.params.absolute;

            // NEW: absolute masks derive their geometry from the ref box - expose it as a draggable/
            // resizable editor handle (object:modified writes back refX/refY/refWidth/refHeight and
            // re-renders, which re-derives the mask from the new bounds - see product-preview _layerRef)
            if(absolute){
                this._addRefRect(null, layer, context, canvas);
            }

            // parallel apply but wait for all to finish
            let clipPathPromises = [];

            // apply mask to objects that reference it
            canvas.forEachObject((obj) => {
                // check if mask reference to this layer
                if(obj._layer && this._isMaskedLayer(layer.params.maskLayers, obj._layer.name)){

                    // get clip path for this specific object
                    clipPathPromises.push(this._getClipPath(layer, context, canvas, obj).then(clipPath => {

                        // NEW: refRect area clip assigned by LayerRenderer._getOptions - mask clips are
                        // tagged with _mask so a previous mask render is not mistaken for the area clip
                        // (re-renders read the refRect back from the nested slot)
                        let refRectClip = null;
                        if(obj.clipPath){
                            refRectClip = obj.clipPath._mask ? (obj.clipPath.clipPath || null) : obj.clipPath;
                        }

                        if(clipPath){
                            clipPath._mask = true;
                            clipPath._maskLayer = layer.name; // the svg export's layer id ( export-url.js designSvg )

                            if(absolute && refRectClip && refRectClip !== clipPath){
                                // nest the area clip so both the box bound and the mask apply
                                clipPath.clipPath = refRectClip;
                            } else if(!absolute && clipPath.clipPath){
                                // cached clipPath reuse (svg) - clear a stale nested clip when absolute is toggled off
                                delete clipPath.clipPath;
                            }

                            obj.clipPath = clipPath;
                        } else if(absolute && refRectClip){
                            obj.clipPath = refRectClip; // keep the area clip when the absolute mask resolves empty
                        } else {
                            delete obj.clipPath; // reset
                        }

                        // NEW: mark as dirty to force re-render (required to update mask)
                        obj.dirty = true;
                    }));

                }

            });

            return Promise.all(clipPathPromises);

        } else {

            // full canvas
            return this._getClipPath(layer, context, canvas).then( clipPath => {

                if(clipPath){

                    // IMPORTANT: flag the context that a mask is active (to support adding/removing mask layers and also auto removing the mask)
                    context.mask = true;

                    // for testing
                    // canvas.backgroundColor = '#424bfc';

                    // show controls over the clipPath THIS FUCKS UP THE BACKGROUND - IS NOT CUT OFF - only confusing
                    // canvas.controlsAboveOverlay = true;

                    canvas.clipPath = clipPath;
                    clipPath._maskLayer = layer.name; // the svg export's layer id ( export-url.js designSvg )

                    // cache reference on layer ( only cached for full canvas )
                    layer._object = clipPath;
                } else {
                    delete canvas.clipPath; // reset

                    delete layer._object;
                }

                return clipPath;
            });
        }

    }

    _isMaskedLayer(maskLayers, layerName){
        if(maskLayers.indexOf(layerName) !== -1){
            return true;
        } else {
            // NEW: overlay values child layer support we have to check substring
            for (let i = 0; i < maskLayers.length; i++) {
                if(layerName.startsWith(maskLayers[i] + '_')){
                    return true;
                }
            }
        }
        return false;
    }

    // docs: http://fabricjs.com/clippath-part1
    _getClipPath(layer, context, canvas, targetObj){

        let options = this._getOptions(layer, context);

        // fabric's default strokeWidth 1 grows every shape's box and shifts the clip off-center - a clip is never stroked
        options.strokeWidth = 0;

        // NEW: per-object mask fixed in canvas coordinates instead of following the object (only meaningful with maskLayers)
        let absolute = !!layer.params.absolute;

        // NEW: absolute masks derive ALL geometry from the unified ref box (one box drives every shape).
        // getRefBounds returns an axis aligned {left,top,width,height} (refAngle baked into a rotated
        // bounding box) and falls back to full canvas when no ref box is configured - so a misconfigured
        // absolute mask fills the canvas (visible) instead of going invisible.
        let bounds = (targetObj && absolute) ? RenderUtil.getRefBounds(layer, context) : null;

        let clipPath = null;

        if(layer.params.shape){
            switch(layer.params.shape){
                case 'circle':

                    // delete cached object if not same type OR a stale full-canvas cache with a different absolute flag
                    if(layer._object && (layer._object.type !== 'circle' || !!layer._object.absolutePositioned !== absolute)){
                        delete layer._object;
                    }

                    clipPath = layer._object || new fabric.Circle();

                    if(layer.params.radius){
                        options.radius = layer.params.radius;
                    } else if(!layer.params.maskLayers || layer.params.maskLayers.length === 0) {
                        let width = context.config.width; // IMPORTANT:  use context defined static width ( canvas.getWidth() is scaled by zoom )
                        options.radius = width / 2;
                    }

                    if(targetObj && !absolute){
                        // if not set fill object
                        if(!layer.params.radius){
                            options.radius = targetObj.width / 2;
                        }

                        // center is in the middle ( auto move x/y for the circle to fill the object )
                        options.left = -options.radius;
                        options.top = -options.radius;
                    } else if(targetObj && absolute){
                        // inscribed circle derived from the ref box (fits inside the box, centered)
                        // IMPORTANT: always overwrite so a stray params.x/angle/scale from _getOptions cannot leak
                        options.radius = Math.min(bounds.width, bounds.height) / 2;
                        options.left = bounds.left + bounds.width / 2 - options.radius;
                        options.top = bounds.top + bounds.height / 2 - options.radius;
                        options.angle = 0;
                        options.scaleX = 1;
                        options.scaleY = 1;
                    }

                    if(targetObj){
                        // IMPORTANT: always set explicitly so toggling the flag off clears it on cached clipPaths (setOptions only writes present keys)
                        options.absolutePositioned = absolute;
                    }

                    clipPath.setOptions(options);

                    break;

                case 'triangle':

                    // delete cached object if not same type OR a stale full-canvas cache with a different absolute flag
                    if(layer._object && (layer._object.type !== 'triangle' || !!layer._object.absolutePositioned !== absolute)){
                        delete layer._object;
                    }

                    clipPath = layer._object || new fabric.Triangle();

                    if(!layer.params.maskLayers || layer.params.maskLayers.length === 0) {
                        options.width = context.config.width; // IMPORTANT:  use context defined static width ( canvas.getWidth() is scaled by zoom )
                        options.height = context.config.height;
                    }

                    if(targetObj && !absolute){
                        options.width = targetObj.width;
                        options.height = targetObj.height;

                        // center is in the middle ( auto move x/y for the triangle to fill the object )
                        options.left = -(options.width / 2);
                        options.top = -(options.height / 2);
                    } else if(targetObj && absolute){
                        // fill the ref box - always overwrite so stray params.x/angle/scale cannot leak
                        options.left = bounds.left;
                        options.top = bounds.top;
                        options.width = bounds.width;
                        options.height = bounds.height;
                        options.angle = 0;
                        options.scaleX = 1;
                        options.scaleY = 1;
                    }

                    if(targetObj){
                        // IMPORTANT: always set explicitly so toggling the flag off clears it on cached clipPaths (setOptions only writes present keys)
                        options.absolutePositioned = absolute;
                    }

                    clipPath.setOptions(options);

                    break;

                case 'rect':

                    // delete cached object if not same type OR a stale full-canvas cache with a different absolute flag
                    if(layer._object && (layer._object.type !== 'rect' || !!layer._object.absolutePositioned !== absolute)){
                        delete layer._object;
                    }

                    clipPath = layer._object || new fabric.Rect();

                    if(!layer.params.maskLayers || layer.params.maskLayers.length === 0) {
                        options.width = context.config.width; // IMPORTANT:  use context defined static width ( canvas.getWidth() is scaled by zoom )
                        options.height = context.config.height;
                    }

                    if(targetObj && !absolute){
                        options.width = targetObj.width;
                        options.height = targetObj.height;

                        // center is in the middle ( auto move x/y for the rect to fill the object )
                        options.left = -(options.width / 2);
                        options.top = -(options.height / 2);
                    } else if(targetObj && absolute){
                        // fill the ref box - always overwrite so stray params.x/angle/scale cannot leak
                        options.left = bounds.left;
                        options.top = bounds.top;
                        options.width = bounds.width;
                        options.height = bounds.height;
                        options.angle = 0;
                        options.scaleX = 1;
                        options.scaleY = 1;
                    }

                    // optional rounded corners (all modes) - IMPORTANT: always set explicitly so removing
                    // the param clears it on a cached clipPath (setOptions only writes present keys)
                    options.rx = layer.params.rx || 0;
                    options.ry = layer.params.ry || 0;

                    if(targetObj){
                        // IMPORTANT: always set explicitly so toggling the flag off clears it on cached clipPaths (setOptions only writes present keys)
                        options.absolutePositioned = absolute;
                    }

                    clipPath.setOptions(options);

                    break;

                case 'svg':

                    // fetch url
                    if(layer.params.url){

                        let svgPromise = null;

                        // CACHE with individual object support - clipPath check if custom _url is matching
                        if(targetObj && targetObj.clipPath && targetObj.clipPath._url === layer.params.url){
                            svgPromise = Promise.resolve(targetObj.clipPath);
                        } else if(canvas.clipPath && canvas.clipPath._url === layer.params.url){
                            svgPromise = Promise.resolve(canvas.clipPath);
                        } else {
                            svgPromise = new Promise((resolve, reject) => {

                                // INFO: we fetch on every render ( this could be improved with caching BUT needs to support caching
                                fabric.loadSVGFromURL(layer.params.url, (objects, options) => {

                                    if (objects) {

                                        // create single svg group
                                        let svgMask = fabric.util.groupSVGElements(objects, options);

                                        resolve(svgMask);

                                    } else {

                                        log.warn('svg mask not found', layer.params.url);

                                        // still resolve and not reject to continue rendering
                                        resolve(null);
                                    }
                                });

                            })
                        }

                        return svgPromise.then(svgMask => {

                            if(svgMask){
                                // store copy of url to check when using cached clipPath
                                options._url = layer.params.url;

                                options.width = svgMask.width;
                                options.height = svgMask.height;

                                // padding pixel to allow for mask path to show - absolute masks fit the ref box exactly
                                let padding = layer.params.padding ?? (absolute ? 0 : 3);

                                if(!layer.params.maskLayers || layer.params.maskLayers.length === 0) {
                                    // scale mask to fit canvas dimensions
                                    options.scaleX = (context.config.width - padding) / svgMask.width; // IMPORTANT:  use context defined static width ( canvas.getWidth() is scaled by zoom )
                                    options.scaleY = (context.config.height - padding) / svgMask.height;
                                } else if(targetObj && !absolute){
                                    options.scaleX = (targetObj.width - padding) / svgMask.width;
                                    options.scaleY = (targetObj.height - padding) / svgMask.height;
                                } else if(targetObj && absolute){
                                    // scale mask to fit the ref box dimensions
                                    options.scaleX = (bounds.width - padding) / svgMask.width;
                                    options.scaleY = (bounds.height - padding) / svgMask.height;
                                }

                                // default scale to keep ratio
                                let ratio = layer.params.ratio || true;
                                if(ratio){
                                    let scale = Math.min(options.scaleX, options.scaleY);
                                    options.scaleX = scale;
                                    options.scaleY = scale;
                                }

                                // adjust mask position after scale is updated
                                if(targetObj && !absolute){
                                    // center is in the middle ( auto move x/y to center )
                                    options.left = -((svgMask.width * options.scaleX) / 2);
                                    options.top = -((svgMask.height * options.scaleY) / 2);
                                } else if(targetObj && absolute){
                                    // center within the ref box - always overwrite so stray params.x/angle cannot leak
                                    options.left = bounds.left + (bounds.width - svgMask.width * options.scaleX) / 2;
                                    options.top = bounds.top + (bounds.height - svgMask.height * options.scaleY) / 2;
                                    options.angle = 0;
                                } else if(!targetObj && layer.params.autoPos){
                                    // full canvas: centre instead of top/left flush - x/y only apply without autoPos, so hand-placed masks stay put
                                    options.left = (context.config.width - svgMask.width * options.scaleX) / 2;
                                    options.top = (context.config.height - svgMask.height * options.scaleY) / 2;
                                }

                                if(targetObj){
                                    // IMPORTANT: always set explicitly so toggling the flag off clears it on the cached clipPath (setOptions only writes present keys)
                                    options.absolutePositioned = absolute;
                                }

                                // NEW: new fabric 4 seems to work with caching
                                // OLD: new fabric 1.7 caching - has to be disabled for svg masks
                                //options.objectCaching = false;

                                svgMask.setOptions(options);
                            }

                            return svgMask;
                        });

                    } else {
                        log.warn('svg mask missing url', layer);
                    }


                    break;
            }
        }

        return Promise.resolve(clipPath);
    }


}

export default MaskRenderer;